// Damage-combination splits ("C2A-60;C3B-40").
// Same grammar as access-method splits (see percentSplits.js). Codes come
// from the Corrosion & Damage master and cannot be created inline.

import {
  buildSplitsValue,
  computeSplitAreas,
  formatSplitAreasInline,
  parseSplitsValue,
  summarizeSplitAreas,
} from "./percentSplits";

/**
 * The stored split wins when present; otherwise fall back to the element's
 * single combination, so an element without a mix reads as one 100% portion.
 * Mirrors the backend's NULL-means-use-the-FK rule.
 */
export const getElementDamageValue = (el) => {
  const split = String(
    el?.damageCombinations ?? el?.damage_combinations ?? "",
  ).trim();
  if (split) return split;

  const combo =
    el?.corrosionDamageCombination || el?.corrosion_damage_combination;
  const code = combo?.combinationCode || combo?.combination_code || "";
  return String(code || "").trim();
};

export const parseDamageCombinationsValue = (rawValue) =>
  parseSplitsValue(rawValue).map((split) => ({
    combinationCode: split.name,
    percentage: split.percentage,
  }));

export const buildDamageCombinationsValue = (rows) =>
  buildSplitsValue(
    rows.map((row) => ({
      name: row.combinationCode,
      percentage: row.percentage,
    })),
  );

export const summarizeDamageAreas = (elements) => {
  const summary = summarizeSplitAreas(elements, getElementDamageValue);
  return {
    entries: summary.entries.map((entry) => ({
      combinationCode: entry.name,
      area: entry.area,
    })),
    unassignedArea: summary.unassignedArea,
  };
};

export const formatDamageAreasInline = (rawValue, area) =>
  formatSplitAreasInline(rawValue, area);

export const computeDamageAreas = (rawValue, area) =>
  computeSplitAreas(rawValue, area).map((split) => ({
    combinationCode: split.name,
    percentage: split.percentage,
    area: split.area,
  }));

/** True when the element carries more than one combination. */
export const hasDamageMix = (el) =>
  parseSplitsValue(getElementDamageValue(el)).length > 1;
